import { NON_BREAKING_SPACE } from "constants/index";

import { ReactNode, useState, VFC } from "react";

import { Business, Check } from "@material-ui/icons";
import { Avatar, Button, ButtonProps } from "@portex-pro/ui-components";
import classNames from "classnames";
import StatusView from "components/StatusView";
import { REACT_APP_SMC3_CARRIER_IMAGE_BUCKET_URL } from "env";
import noop from "lodash/noop";
import uniq from "lodash/uniq";
import { useTranslation } from "react-i18next";

export interface CarrierTileViewProps {
  avatar?: JSX.Element | ReactNode;
  connected?: boolean;
  name?: string;
  tags?: string[];
  onClick?: () => void;
  scac: string;
}

const CarrierTileView: VFC<CarrierTileViewProps> = ({
  avatar,
  connected = false,
  name,
  tags,
  onClick = noop,
  scac,
}) => {
  const { t } = useTranslation(["shipper"]);
  const connectedButtonPropsMap: Record<number, ButtonProps> = {
    1: {
      fullWidth: true,
      variant: "outlined",
      color: "default",
      children: t("shipper:viewDetails"),
    },
    0: {
      fullWidth: true,
      variant: "contained",
      color: "primary",
      children: t("shipper:connect"),
    },
  };

  const [imageLoadError, setImageLoadError] = useState(false);

  return (
    <div
      className={classNames(
        "border border-border rounded",
        "bg-white hover:bg-grey-50",
        "min-w-[296px] w-[296px] 2xl:min-w-[254px] 2xl:w-[254px] min-h-[330px] p-2",
        "flex flex-col items-center"
      )}
      onClick={onClick}
    >
      <div className="self-end">
        {connected ? (
          <StatusView pill className="text-green-500 bg-green-100 normal-case">
            <Check className="mt-[-5px] mr-1" />
            <span>{t("shipper:connected")}</span>
          </StatusView>
        ) : (
          NON_BREAKING_SPACE
        )}
      </div>
      <div className="mt-6 flex flex-col items-center">
        <div className="w-[109px] h-[109px]">
          {!!avatar ? (
            avatar
          ) : (
            <Avatar
              className="cursor-pointer [&&]:w-full [&&]:h-full [&&]:rounded-2xl [&&]:border [&&]:border-grey-100 [&&]:bg-grey-50"
              variant="rounded"
            >
              {imageLoadError ? (
                <Business className="[&&]:w-[80%] [&&]:h-[80%] text-grey-600" />
              ) : (
                <img
                  src={`${REACT_APP_SMC3_CARRIER_IMAGE_BUCKET_URL}/${scac}`}
                  onError={() => setImageLoadError(true)}
                />
              )}
            </Avatar>
          )}
        </div>
        <div className="py-2 flex flex-col gap-1">
          <div className="font-bold text-medium">{name || NON_BREAKING_SPACE}</div>
          <div className="flex gap-x-1 gap-y-1 text-nowrap flex-wrap justify-center">
            {!!tags?.length
              ? uniq(tags).map((tag) => (
                  <StatusView key={`tag_${tag}`} className="bg-grey-100 text-small text-black normal-case font-medium">
                    {tag}
                  </StatusView>
                ))
              : NON_BREAKING_SPACE}
          </div>
        </div>
      </div>
      <div className="mt-auto w-full">
        <Button {...connectedButtonPropsMap[+connected]} onClick={onClick} />
      </div>
    </div>
  );
};

export default CarrierTileView;
